import React, { useState, useCallback, lazy, Suspense } from 'react';
import videoService from '../../services/videoService';
import VideoPlayer from './VideoPlayer';
import VideoAssignmentView from './VideoAssignmentView';
import VideoExportButton from './VideoExportButton';
import QuizFlashcardEditorModal from '../common/QuizFlashcardEditorModal';

const VideoDiagramEditorModal = lazy(() => import('./VideoDiagramEditorModal'));
const VideoExcalidrawModal = lazy(() => import('./VideoExcalidrawModal'));

const THEMES = ['blue', 'green', 'purple', 'orange', 'red', 'teal', 'indigo', 'gray', 'sky'];
const THEME_COLORS = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
  teal: 'bg-teal-500',
  indigo: 'bg-indigo-500',
  gray: 'bg-gray-500',
  sky: 'bg-sky-500',
};

export default function VideoEditorPanel({
  video: initialVideo,
  onClose,
  target = null,
  userRole = 'parent',
  showAssign = true,
  onAssigned = null,
  renderAsPage = false
}) {
  const [video, setVideo] = useState(initialVideo);
  const [slides, setSlides] = useState(initialVideo?.slides || []);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [audioGenerating, setAudioGenerating] = useState(false);
  const [slideAudioGenerating, setSlideAudioGenerating] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Diagram modal state
  const [diagramModal, setDiagramModal] = useState(null); // null | 'svg' | 'excalidraw'

    // Quiz editor modal — opens the shared QuizFlashcardEditorModal with
  // the video's flashcard quiz (the same one VideoPlayer renders after
  // the last slide).
  const [quizModalOpen, setQuizModalOpen] = useState(false);
  const quizQuestions = video?.quiz?.questions || [];

const selected = slides[selectedIndex];

  const updateSlide = useCallback((index, updates) => {
    setSlides((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, []);

  const updateBullet = (idx, value) => {
    const bullets = [...(selected.bullet_points || [])];
    bullets[idx] = value;
    updateSlide(selectedIndex, { bullet_points: bullets });
  };

  const addBullet = () => {
    const bullets = [...(selected.bullet_points || []), ''];
    updateSlide(selectedIndex, { bullet_points: bullets });
  };

  const removeBullet = (idx) => {
    const bullets = (selected.bullet_points || []).filter((_, i) => i !== idx);
    updateSlide(selectedIndex, { bullet_points: bullets });
  };

  const addSlide = () => {
    const newSlide = {
      slide_id: `local-${Date.now()}`,
      order: slides.length,
      title: 'New Slide',
      body: '',
      bullet_points: ['Point 1', 'Point 2'],
      narration: '',
      audio_b64: null,
      audio_duration_s: null,
      theme: THEMES[slides.length % THEMES.length],
      diagram_svg: '',
      excalidraw_elements: null,
      excalidraw_files: null,
    };
    setSlides((prev) => [...prev, newSlide]);
    setSelectedIndex(slides.length);
  };

  const deleteSlide = () => {
    if (slides.length <= 1) return;
    const next = slides.filter((_, i) => i !== selectedIndex);
    setSlides(next);
    setSelectedIndex(Math.min(selectedIndex, next.length - 1));
  };

  const moveSlide = (direction) => {
    const targetIndex = selectedIndex + direction;
    if (targetIndex < 0 || targetIndex >= slides.length) return;
    const next = [...slides];
    [next[selectedIndex], next[targetIndex]] = [next[targetIndex], next[selectedIndex]];
    setSlides(next);
    setSelectedIndex(targetIndex);
  };

  const saveSlides = async () => {
    setSaving(true);
    try {
      await videoService.updateSlides(video.video_id, slides);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch {
      setSaveMsg('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const generateAllAudio = async () => {
    setAudioGenerating(true);
    try {
      await saveSlides();
      await videoService.generateAllAudio(video.video_id, video.voice);
      const res = await videoService.getById(video.video_id);
      setSlides(res.data.slides || slides);
      setVideo(res.data);
      setSaveMsg('Audio generated');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch {
      setSaveMsg('Audio failed');
    } finally {
      setAudioGenerating(false);
    }
  };

  const regenerateSlideAudio = async (slideId) => {
    setSlideAudioGenerating(slideId);
    try {
      const res = await videoService.regenerateSlideAudio(video.video_id, slideId, video.voice);
      const updatedSlide = res.data?.slide;
      if (updatedSlide) {
        setSlides((prev) => prev.map((s) =>
          s.slide_id === slideId ? { ...s, audio_b64: updatedSlide.audio_b64 } : s
        ));
      }
    } catch {
      setSaveMsg('Audio failed');
    } finally {
      setSlideAudioGenerating(null);
    }
  };

  const previewVideo = async () => {
    await saveSlides();
    setShowPlayer(true);
  };

  // Diagram handlers
  const handleSaveSvgDiagram = (svgCode) => {
    updateSlide(selectedIndex, { diagram_svg: svgCode, excalidraw_elements: null });
    setDiagramModal(null);
  };

  const handleSaveExcalidraw = (svgCode, elements, files) => {
    updateSlide(selectedIndex, { diagram_svg: svgCode, excalidraw_elements: elements, excalidraw_files: files || null });
    setDiagramModal(null);
  };

  if (showPlayer) {
    return (
      <VideoPlayer
        video={{ ...video, slides }}
        onClose={() => setShowPlayer(false)}
      />
    );
  }

  return (
    <div className={renderAsPage ? 'w-full max-w-7xl mx-auto px-0 sm:px-6 lg:px-8' : 'fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-4 px-2'}>
      <div
        className={`bg-white w-full mx-auto flex flex-col ${renderAsPage ? 'shadow-lg rounded-none sm:rounded-2xl' : 'rounded-2xl shadow-2xl max-w-5xl'}`}
        style={renderAsPage ? undefined : { minHeight: '90vh' }}
      >
        {/* Header — close button is absolutely positioned in the top-right corner
            so it stays put even when the action buttons wrap on small screens. */}
        <div className="relative px-6 py-4 border-b">
          {/* Close button — always top-right */}
          <button
            onClick={onClose}
            className="absolute top-3 right-4 z-10 text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-gray-200 transition-all"
            aria-label="Close"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>

          <div className="flex items-start gap-2 flex-wrap pr-8">
            {/* Title */}
            <div className="min-w-0 mr-2">
              <h2 className="text-lg font-bold text-gray-900 truncate">{video.title}</h2>
              <p className="text-xs text-gray-500">{slides.length} slides</p>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap">
              {saveMsg && (
                <span className="text-sm text-green-600 font-medium">
                  {saveMsg}
                </span>
              )}

              <button
                onClick={previewVideo}
                className="flex items-center gap-1 sm:gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm font-semibold px-1 py-1 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                ▶ Preview
              </button>

              <button
                onClick={generateAllAudio}
                disabled={audioGenerating}
                className="flex items-center gap-1 sm:gap-1.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white text-xs sm:text-sm font-semibold px-1 py-1 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                {audioGenerating ? 'Generating...' : '🔊 All Audio'}
              </button>

              <button
                onClick={saveSlides}
                disabled={saving}
                className="flex items-center gap-1 sm:gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs sm:text-sm font-semibold px-1 py-1 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>

              <button
                onClick={() => setQuizModalOpen(true)}
                title="Preview & edit flashcards"
                className="flex items-center gap-1 sm:gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-semibold px-1 py-1 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                🎯 {quizQuestions.length > 0 ? `Quiz (${quizQuestions.length})` : 'Add Quiz'}
              </button>

              <VideoExportButton
                videoId={video.video_id}
                videoTitle={video.title}
              />

              {showAssign && (
                <VideoAssignmentView
                  video={video}
                  target={target}
                  userRole={userRole}
                  onAssigned={onAssigned}
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left: Slide list */}
          <div className="w-48 sm:w-56 border-r overflow-y-auto flex-shrink-0">
            <div className="p-3 space-y-2">
              {slides.map((s, i) => (
                <button
                  key={s.slide_id || i}
                  onClick={() => setSelectedIndex(i)}
                  className={`w-full text-left p-3 rounded-lg border transition-all text-sm ${
                    i === selectedIndex
                      ? 'border-blue-400 bg-blue-50 font-semibold text-blue-800'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${THEME_COLORS[s.theme] || 'bg-gray-400'}`} />
                    <span className="text-xs text-gray-400">{i + 1}</span>
                    {s.audio_b64 && <span className="text-green-500 text-xs">♪</span>}
                    {s.diagram_svg && <span className="text-violet-400 text-xs">◈</span>}
                  </div>
                  <div className="truncate leading-tight">{s.title || `Slide ${i + 1}`}</div>
                </button>
              ))}
            </div>
            <div className="p-3 border-t">
              <button
                onClick={addSlide}
                className="w-full text-xs text-blue-600 hover:text-blue-700 font-medium py-1.5 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
              >
                + Add Slide
              </button>
            </div>
          </div>

          {/* Right: Edit form */}
          {selected ? (
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Slide toolbar */}
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => moveSlide(-1)} disabled={selectedIndex === 0} className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 border rounded px-2 py-1">↑ Up</button>
                <button onClick={() => moveSlide(1)} disabled={selectedIndex === slides.length - 1} className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 border rounded px-2 py-1">↓ Down</button>
                {/* Diagram buttons */}
                <button
                  onClick={() => setDiagramModal('svg')}
                  className="text-xs text-violet-600 hover:text-violet-700 border border-violet-200 rounded px-2 py-1 hover:bg-violet-50 transition-colors"
                  title="Add or edit SVG diagram (Option A)"
                >
                  ◈ {selected.diagram_svg ? 'Edit Diagram' : 'Add Diagram'}
                </button>
                <button
                  onClick={() => setDiagramModal('excalidraw')}
                  className="text-xs text-violet-600 hover:text-violet-700 border border-violet-200 rounded px-2 py-1 hover:bg-violet-50 transition-colors"
                  title="Sketch a freehand diagram with Excalidraw (Option B)"
                >
                  ✏ {selected.excalidraw_elements?.length ? 'Edit Sketch' : 'Sketch Diagram'}
                </button>
                <button onClick={deleteSlide} disabled={slides.length <= 1} className="text-xs text-red-400 hover:text-red-600 disabled:opacity-30 border border-red-200 rounded px-2 py-1 ml-auto">Delete Slide</button>
              </div>

              {/* Diagram preview strip */}
              {selected.diagram_svg && (
                <div className="border border-violet-200 rounded-xl p-3 bg-violet-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-violet-700">Diagram attached</span>
                    <button
                      onClick={() => updateSlide(selectedIndex, { diagram_svg: '', excalidraw_elements: null, excalidraw_files: null })}
                      className="text-xs text-red-400 hover:text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                  <div
                    className="h-36 rounded-lg bg-white border border-violet-100 overflow-hidden
                                flex items-center justify-center
                                [&_svg]:max-w-full [&_svg]:max-h-full
                                [&_svg]:w-auto [&_svg]:h-auto"
                    dangerouslySetInnerHTML={{ __html: selected.diagram_svg }}
                  />
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={selected.title || ''}
                  onChange={(e) => updateSlide(selectedIndex, { title: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Summary (one sentence)</label>
                <input
                  type="text"
                  value={selected.body || ''}
                  onChange={(e) => updateSlide(selectedIndex, { body: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Bullet points */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Bullet Points</label>
                <div className="space-y-2">
                  {(selected.bullet_points || []).map((bp, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        type="text"
                        value={bp}
                        onChange={(e) => updateBullet(i, e.target.value)}
                        className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                      <button
                        onClick={() => removeBullet(i)}
                        className="text-red-400 hover:text-red-600 text-lg font-bold px-1"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={addBullet}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  + Add point
                </button>
              </div>

              {/* Narration */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Narration (spoken text)</label>
                <textarea
                  value={selected.narration || ''}
                  onChange={(e) => updateSlide(selectedIndex, { narration: e.target.value })}
                  rows={4}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
                />
                <button
                  onClick={() => regenerateSlideAudio(selected.slide_id)}
                  disabled={slideAudioGenerating === selected.slide_id || !selected.narration}
                  className="mt-1.5 text-sm text-green-600 hover:text-green-700 font-medium disabled:opacity-40"
                >
                  {slideAudioGenerating === selected.slide_id ? 'Generating...' : selected.audio_b64 ? '🔄 Regenerate Audio' : '🔊 Generate Audio'}
                </button>
              </div>

              {/* Theme */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Theme Color</label>
                <div className="flex gap-2">
                  {THEMES.map((t) => (
                    <button
                      key={t}
                      onClick={() => updateSlide(selectedIndex, { theme: t })}
                      className={`w-8 h-8 rounded-full ${THEME_COLORS[t]} transition-all ${selected.theme === t ? 'ring-2 ring-offset-2 ring-gray-600 scale-110' : 'opacity-70 hover:opacity-100'}`}
                      title={t}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              Select a slide to edit
            </div>
          )}
        </div>
      </div>

      {/* Diagram modals */}
      <Suspense fallback={null}>
        {diagramModal === 'svg' && selected && (
          <VideoDiagramEditorModal
            slide={selected}
            onSave={handleSaveSvgDiagram}
            onClose={() => setDiagramModal(null)}
          />
        )}
        {diagramModal === 'excalidraw' && selected && (
          <VideoExcalidrawModal
            slide={selected}
            onSave={handleSaveExcalidraw}
            onClose={() => setDiagramModal(null)}
          />
        )}
      </Suspense>

      {/* Flashcard quiz editor — same shared component the Lesson Kit
          copilot uses. Saving re-fetches the video so the badge count
          + downstream VideoPlayer flashcards stay in sync. */}
      {quizModalOpen && (
        <QuizFlashcardEditorModal
          initialQuestions={quizQuestions}
          title="Preview & edit flashcards"
          onClose={() => setQuizModalOpen(false)}
          onSave={async (questions) => {
            await videoService.updateQuiz(video.video_id, questions);
            const res = await videoService.getById(video.video_id);
            setVideo(res.data);
            setSaveMsg('Quiz saved');
            setTimeout(() => setSaveMsg(''), 2000);
            setQuizModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
